import React from 'react'
import styled from 'styled-components'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { sizes } from '../utils/breakpoints'
import { getLangFromPath, withLang } from '../utils/i18nRoutes'

const ServicioAutenticidadContenido = () => {
  const { t } = useTranslation()
  const location = useLocation()
  const lang = getLangFromPath(location.pathname)
  const audience = t('serviciosDetalle.c2pa.audience.items', {
    returnObjects: true,
  })
  const contactHref = withLang('/contacto', lang)

  return (
    <Container>
      <BackLink to={withLang('/servicios', lang)}>
        {t('serviciosDetalle.backLink')}
      </BackLink>

      <Hero>
        <Eyebrow>{t('serviciosDetalle.c2pa.eyebrow')}</Eyebrow>
        <Title>{t('serviciosDetalle.c2pa.title')}</Title>
        <Subhead>{t('serviciosDetalle.c2pa.subhead')}</Subhead>
        <CtaRow>
          <PrimaryButton to={contactHref}>
            {t('serviciosDetalle.c2pa.ctaPrimary')}
          </PrimaryButton>
          <SecondaryButton href="#que-hacemos">
            {t('serviciosDetalle.c2pa.ctaSecondary')}
          </SecondaryButton>
        </CtaRow>
      </Hero>

      <Section>
        <SectionHeader>
          <Kicker>{t('serviciosDetalle.c2pa.problem.kicker')}</Kicker>
          <SectionTitle>{t('serviciosDetalle.c2pa.problem.title')}</SectionTitle>
        </SectionHeader>
        <SectionPanel>
          <TextBlock>
            <Text>{t('serviciosDetalle.c2pa.problem.text')}</Text>
            <Emphasis>{t('serviciosDetalle.c2pa.problem.emphasis')}</Emphasis>
          </TextBlock>
        </SectionPanel>
      </Section>

      <Section id="que-hacemos">
        <SectionHeader>
          <Kicker>{t('serviciosDetalle.c2pa.whatWeDo.kicker')}</Kicker>
          <SectionTitle>{t('serviciosDetalle.c2pa.whatWeDo.title')}</SectionTitle>
        </SectionHeader>
        <SectionPanel>
          <TextBlock>
            {t('serviciosDetalle.c2pa.whatWeDo.paragraphs', {
              returnObjects: true,
            }).map((paragraph) => (
              <Text key={paragraph}>{paragraph}</Text>
            ))}
            <Text>{t('serviciosDetalle.c2pa.whatWeDo.note')}</Text>
          </TextBlock>
        </SectionPanel>
      </Section>

      <Section>
        <SectionHeader>
          <Kicker>{t('serviciosDetalle.c2pa.audience.kicker')}</Kicker>
          <SectionTitle>{t('serviciosDetalle.c2pa.audience.title')}</SectionTitle>
        </SectionHeader>
        <SectionPanel $wide>
          <AudienceGrid>
            {audience.map((item, index) => (
              <AudienceCard key={item.title}>
                <AudienceNumber>
                  {String(index + 1).padStart(2, '0')}
                </AudienceNumber>
                <AudienceTitle>{item.title}</AudienceTitle>
                <AudienceText>{item.text}</AudienceText>
              </AudienceCard>
            ))}
          </AudienceGrid>
        </SectionPanel>
      </Section>

      <FinalCta>
        <FinalCtaTitle>{t('serviciosDetalle.c2pa.finalCta.title')}</FinalCtaTitle>
        <FinalCtaText>{t('serviciosDetalle.c2pa.finalCta.text')}</FinalCtaText>
        <PrimaryButton to={contactHref}>
          {t('serviciosDetalle.c2pa.finalCta.button')}
        </PrimaryButton>
      </FinalCta>
    </Container>
  )
}

export default ServicioAutenticidadContenido

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 40px;
  box-sizing: border-box;
  max-width: 900px;
  margin: 0 auto;

  @media (max-width: ${sizes.mobile}px) {
    padding: 0 20px;
  }
`

const BackLink = styled(Link)`
  align-self: flex-start;
  font-family: ${({ theme }) => theme.fonts.accentBold};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  text-decoration: none;
  margin-bottom: 24px;

  &:hover {
    color: ${({ theme }) => theme.colors.text.accent};
  }
`

const Hero = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  margin-bottom: 16px;
`

const Eyebrow = styled.span`
  display: inline-block;
  font-family: ${({ theme }) => theme.fonts.accentBold};
  font-size: 12px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.brand.dark};
  background-color: ${({ theme }) => theme.colors.brand.green};
  padding: 5px 12px;
  border-radius: 999px;
  margin-bottom: 16px;
`

const Title = styled.h1`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: clamp(28px, 4vw, 38px);
  line-height: 1.2;
  color: ${({ theme }) => theme.colors.text.primary};
  max-width: 680px;
  margin: 0 0 14px;
`

const Subhead = styled.p`
  font-family: ${({ theme }) => theme.fonts.accentRegular};
  font-size: 17px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.secondary};
  max-width: 620px;
  margin: 0 0 28px;

  @media (max-width: ${sizes.mobile}px) {
    font-size: 14px;
  }
`

const CtaRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 14px;
  flex-wrap: wrap;

  @media (max-width: ${sizes.mobile}px) {
    flex-direction: column;
  }
`

const PrimaryButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: ${({ theme }) => theme.fonts.accentBold};
  font-size: 15px;
  color: ${({ theme }) => theme.colors.brand.dark};
  background-color: ${({ theme }) => theme.colors.brand.green};
  padding: 13px 26px;
  border-radius: 999px;
  text-decoration: none;
  transition: background-color 0.15s ease, transform 0.15s ease;

  &:hover {
    background-color: ${({ theme }) => theme.colors.brand.dark};
    color: ${({ theme }) => theme.colors.text.onDark};
    transform: translateY(-2px);
  }
`

const SecondaryButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: ${({ theme }) => theme.fonts.accentBold};
  font-size: 15px;
  color: ${({ theme }) => theme.colors.text.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.input};
  padding: 12px 26px;
  border-radius: 999px;
  text-decoration: none;
  transition: border-color 0.15s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.text.accent};
  }
`

const Section = styled.section`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 48px 0;

  &:first-of-type {
    padding-top: 0;
  }
`

const SectionHeader = styled.div`
  max-width: 620px;
  text-align: center;
  margin-bottom: 24px;
`

const Kicker = styled.span`
  display: block;
  font-family: ${({ theme }) => theme.fonts.accentBold};
  font-size: 12px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.text.accent};
  margin-bottom: 8px;
`

const SectionTitle = styled.h2`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: 22px;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;

  @media (max-width: ${sizes.mobile}px) {
    font-size: 18px;
  }
`

const SectionPanel = styled.div`
  width: 100%;
  max-width: ${({ $wide }) => ($wide ? '860px' : '720px')};
  box-sizing: border-box;
  display: flex;
  justify-content: center;
  background-color: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.03)'};
  border-radius: 20px;
  padding: 32px;

  @media (max-width: ${sizes.mobile}px) {
    padding: 20px;
    border-radius: 14px;
  }
`

const TextBlock = styled.div`
  width: 100%;
  max-width: 680px;
  text-align: left;
`

const Text = styled.p`
  font-family: ${({ theme }) => theme.fonts.accentRegular};
  font-size: 15px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 12px;
`

const Emphasis = styled.p`
  border-left: 3px solid ${({ theme }) => theme.colors.brand.dark};
  padding-left: 18px;
  margin: 20px 0 0;
  font-family: ${({ theme }) => theme.fonts.accentSemi};
  font-size: 16px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.primary};
`

const AudienceGrid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;

  @media (max-width: ${sizes.mobile}px) {
    grid-template-columns: 1fr;
  }
`

const AudienceCard = styled.div`
  background-color: ${({ theme }) => theme.colors.background.card};
  box-shadow: ${({ theme }) => theme.shadows.card};
  border-radius: 14px;
  padding: 22px;
`

const AudienceNumber = styled.p`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.brand.green};
  background-color: ${({ theme }) => theme.colors.brand.dark};
  width: 28px;
  height: 28px;
  line-height: 28px;
  text-align: center;
  border-radius: 8px;
  margin: 0 0 14px;
`

const AudienceTitle = styled.p`
  font-family: ${({ theme }) => theme.fonts.accentBold};
  font-size: 16px;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 6px;
`

const AudienceText = styled.p`
  font-family: ${({ theme }) => theme.fonts.accentRegular};
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0;
`

const FinalCta = styled.div`
  width: 100%;
  background-color: ${({ theme }) => theme.colors.background.card};
  box-shadow: ${({ theme }) => theme.shadows.card};
  border-radius: 20px;
  padding: 40px;
  text-align: center;
  margin: 24px 0 56px;

  @media (max-width: ${sizes.mobile}px) {
    padding: 28px 20px;
  }
`

const FinalCtaTitle = styled.h2`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: 24px;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 10px;
`

const FinalCtaText = styled.p`
  font-family: ${({ theme }) => theme.fonts.accentRegular};
  font-size: 15px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.secondary};
  max-width: 500px;
  margin: 0 auto 22px;
`
